import { ApiPermissions, ExchangeStatus } from "@cede/types";
import { TWO_KEYS_PERMISSIONS_WITH_PROXY, TWO_KEYS_PERMISSIONS_WITHOUT_PROXY } from "../constants";
import { advancedRandomId } from "../random";
import { CEDE_CEX_SIGN_UP_URLS, CEDE_CEXS, EXCHANGE_LABELS, PERPS_ONLY_EXCHANGES } from "./constants";

/**
 * Get the display label for any exchange (CEX or DEX)
 * For DEX IDs (format: {protocol}|{chain}), generates a human-readable label if not found in EXCHANGE_LABELS
 */
export const getExchangeLabel = (exchangeId: string): string => {
  // Check if we have an explicit label
  if (EXCHANGE_LABELS[exchangeId]) {
    return EXCHANGE_LABELS[exchangeId];
  }

  // For DEX IDs without explicit labels, generate a readable format
  if (exchangeId.includes("|")) {
    const [protocol, chain] = exchangeId.split("|");
    if (!protocol || !chain) return "Unknown";
    // Capitalize protocol name and clean up suffixes
    const protocolName = protocol
      .replace(/-cl$|-v\d+$/i, "") // Remove -cl, -v3, etc.
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join("");
    const chainName = chain.charAt(0).toUpperCase() + chain.slice(1);
    return `${protocolName} (${chainName})`;
  }

  return exchangeId;
};

/**
 * @deprecated Use getExchangeLabel instead - this is kept for backward compatibility
 */
export const getCexLabel = getExchangeLabel;

export const getCexSignUpUrl = (cexValue: string): string => {
  return CEDE_CEX_SIGN_UP_URLS[cexValue] || "";
};

/**
 * Computes the account id for a given cexId and key. The key can be the API key or the access token.
 */
export const computeExchangeInstanceId = (): string => {
  return advancedRandomId(5);
};

/**
 * Check if the permissions include some of the proxied permissions.
 */
export function hasPermissionsWithProxy(permissions: ApiPermissions[], cex: string): boolean {
  return permissions.some((permission) => TWO_KEYS_PERMISSIONS_WITH_PROXY[cex]?.includes(permission));
}

export function hasPermissionsWithoutProxy(permissions: ApiPermissions[], cex: string): boolean {
  return permissions.some((permission) => TWO_KEYS_PERMISSIONS_WITHOUT_PROXY[cex]?.includes(permission));
}

export function externalExchangeStatusToInternalExchangeStatus(externalStatus?: string): ExchangeStatus {
  for (const key in ExchangeStatus) {
    const internalExchangeStatus = ExchangeStatus[key as keyof typeof ExchangeStatus];
    if (internalExchangeStatus === externalStatus) {
      return internalExchangeStatus;
    }
  }
  return ExchangeStatus.Unknown;
}

export type MarketType = "spot" | "perps";

/**
 * Determines the market type based on exchange and pair symbol.
 * Used for analytics tracking to distinguish spot vs perpetual trades.
 *
 * Logic:
 * - Perps-only exchanges (e.g., Kraken Futures) → always "perps"
 * - Any exchange: perps commonly use ":" in pairSymbol (e.g., "BTC:USDC"), spot doesn't
 * - All other exchanges → "spot"
 *
 * @param exchangeId - The exchange ID (e.g., "hyperliquid", "krakenfutures")
 * @param pairSymbol - The trading pair symbol (e.g., "BTC/USDT", "BTC:USDC")
 * @returns "spot" or "perps"
 */
export const getMarketType = (exchangeId: string, pairSymbol: string): MarketType => {
  // Perps-only exchanges (e.g., Kraken Futures) are always perps
  if (PERPS_ONLY_EXCHANGES.has(exchangeId as CEDE_CEXS)) {
    return "perps";
  }

  // Perps commonly use ":" in symbol (e.g., "BTC:USDC"), spot doesn't
  if (pairSymbol.includes(":")) {
    return "perps";
  }

  // Default to spot for all other CEX trades
  return "spot";
};
